// ---------- la vidéo du remplissage ----------
// Une feuille terminée, rejouée case par case DANS L'ORDRE OÙ LE JOUEUR L'A PEINTE, filmée en une
// vidéo de quelques secondes qu'il peut partager (cf. shareVideo dans ui.ts). Demandée le
// 2026-10-16, après le partage en image des créations.
//
// L'ordre est celui de la sauvegarde (`S.taps`) : c'est déjà le script du « Revoir l'animation »
// de fin de partie (cf. mergeTaps côté serveur, qui le garde). La vidéo ne montre donc pas un
// remplissage inventé, mais le vrai — par zones quand le joueur a peint par zones, en tournant
// autour du visage s'il a commencé par là.
//
// Enregistrée par le navigateur lui-même (`canvas.captureStream` + `MediaRecorder`) : aucune
// bibliothèque, et le même code dans Chrome et dans la WebView de l'application. Le prix est le
// TEMPS RÉEL — huit secondes de vidéo se fabriquent en huit secondes, d'où la progression que
// l'appelant affiche.
//
// Le format : MP4 quand le navigateur sait l'écrire (Chrome récent), WebM sinon. Le WebM passe mal
// dans plusieurs messageries ; s'il pose problème à l'usage, c'est un encodeur MP4 dédié qu'il
// faudra (WebCodecs et un multiplexeur), pas un autre réglage ici.

import { alphabetById, paintOrder } from '../shared/alphabets';
import { pal } from './palette';
import { brandBand, brandPainter } from './brand';
import { noneStitched, placeCell } from './stitchedSoFar';

/** La durée du remplissage. Assez court pour une messagerie, assez long pour voir le dessin
 *  naître. 7 s jusqu'au 2026-10-17 ; 6 depuis que la loupe qui suit allonge la vidéo. */
const FILL_MS = 6000;
/**
 * LA LOUPE SUR LE DESSIN FINI (demandée le 2026-10-17) : « qu'on se rende compte de comment est
 * constitué le puzzle vu de près ». Un temps sur le dessin entier, la plongée vers l'endroit le
 * plus détaillé, un temps pour lire les touches une à une, la remontée, et la pose finale — qui
 * remplace l'ancienne pose de 1,5 s.
 *
 * 9,9 s en tout : la première version durait 10,8 s, plongeait moins près (26 cases de large) et
 * plus lentement ; « un petit peu plus important, un peu plus rapide, sans dépasser 10 secondes »
 * au premier essai.
 */
const LOUPE = { rest: 300, dive: 1100, close: 900, rise: 1000, end: 600 } as const;
const AFTER_MS = LOUPE.rest + LOUPE.dive + LOUPE.close + LOUPE.rise + LOUPE.end;
/** Combien de cases tiennent en largeur au plus près : seize, de quoi voir chaque touche comme un
 *  objet — sa forme, ses poils — sans perdre de vue ce qu'elle dessine (26 au premier essai). */
const CLOSE_CELLS = 16;
/** La loupe ne grossit jamais moins que ça (une petite grille est déjà lisible de loin, mais une
 *  plongée timide ne se remarquerait pas) ni plus (au-delà, on ne voit plus qu'une touche). */
const MIN_ZOOM = 2.5;
const MAX_ZOOM = 7;
/** Le plus grand côté du rendu de la feuille ENTIÈRE, celui de la plongée et de la remontée.
 *  2 048 px : ~10 Mo pour une feuille en portrait. Le gros plan, lui, a son propre rendu (cf.
 *  REGION_MARGIN), sans quoi ce plafond aurait montré des touches floues au plus près. */
const DETAIL_MAX_PX = 2048;
/**
 * LE GROS PLAN : la zone visée seule, rendue à la résolution EXACTE du plus près — chaque touche y
 * est nette, quel que soit le grossissement. Une fois et demie la vue la plus serrée de côté, pour
 * que la fin de la plongée et le début de la remontée y tiennent aussi : ~2,3 fois la taille de
 * la feuille dans la vidéo, soit ~12 Mo. Les deux rendus se fondent l'un dans l'autre pendant la
 * plongée (cf. la boucle), sans saut de netteté.
 */
const REGION_MARGIN = 1.5;
const FPS = 30;
/** La largeur de la vidéo — celle d'une vidéo verticale de téléphone ; la hauteur suit la feuille,
 *  plafonnée au format 9:16. Paires, parce que les encodeurs vidéo travaillent par blocs de 2. */
const WIDTH = 1080;
const MAX_HEIGHT = 1920;

export interface FillSource {
  gridW: number;
  gridH: number;
  target: readonly number[][];
  alphabetId: string;
  /** L'ordre de peinture, en indices de cases (r × gridW + c). */
  taps: readonly number[];
}

/** Le premier format que ce navigateur sait enregistrer, MP4 en tête. Vide : pas de vidéo ici. */
export function videoMime(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  for (const m of [
    'video/mp4;codecs=avc1',
    'video/mp4',
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
  ])
    if (MediaRecorder.isTypeSupported(m)) return m;
  return '';
}

function even(n: number): number {
  return Math.max(2, Math.round(n / 2) * 2);
}

/**
 * Filme le remplissage. `onProgress` reçoit 0..1 au fil de l'enregistrement. Rend la vidéo et son
 * type ; lève si le navigateur ne sait pas enregistrer.
 */
export async function recordFillVideo(
  src: FillSource,
  onProgress: (q: number) => void,
): Promise<{ blob: Blob; mime: string }> {
  const mime = videoMime();
  if (!mime) throw new Error('MediaRecorder unavailable');
  const alpha = alphabetById(src.alphabetId);
  const ar = alpha.aspect();
  const paintBrand = await brandPainter();

  // La feuille, posée en haut d'un cadre de WIDTH de large, avec une marge de papier — une vidéo
  // dont le dessin touche les bords se lit mal en vignette de messagerie —, et sous elle la bande
  // de signature : le logo et le nom (cf. src/brand.ts).
  const margin = Math.round(WIDTH * 0.06);
  const band = brandBand(WIDTH);
  const inner = WIDTH - 2 * margin;
  const cell = Math.min(inner / (src.gridW * ar), (MAX_HEIGHT - 2 * margin - band) / src.gridH);
  const cw = cell * ar;
  const sheetW = src.gridW * cw;
  const sheetH = src.gridH * cell;
  const cv = document.createElement('canvas');
  cv.width = even(WIDTH);
  cv.height = even(sheetH + 2 * margin + band);
  const ox = (cv.width - sheetW) / 2;
  const oy = margin;
  const g = cv.getContext('2d')!;
  const p = pal();

  // Le papier et ses réglures, comme la feuille avant qu'on y touche.
  g.fillStyle = p.paper;
  g.fillRect(0, 0, cv.width, cv.height);
  rule(g, src, alpha.bareSheet && !alpha.ruledSheet, ox, oy, cw, cell, p.ruling);
  // La signature, posée dès la première image : la vidéo dit d'où elle vient du début à la fin.
  paintBrand(g, ox, ox + sheetW, oy + sheetH + (cv.height - oy - sheetH) / 2, band);

  // L'ordre : les taps de la sauvegarde, puis — filet — les cases peintes qu'ils ne nommeraient
  // pas (une sauvegarde d'avant le remplissage case par case ne portait que la graine des zones).
  const order: [number, number][] = [];
  const seen = new Uint8Array(src.gridW * src.gridH);
  for (const idx of src.taps) {
    const r = Math.floor(idx / src.gridW);
    const c = idx % src.gridW;
    if (r >= src.gridH || seen[idx] || !src.target[r][c]) continue;
    seen[idx] = 1;
    order.push([r, c]);
  }
  for (const [r, c] of paintOrder(src.target, alpha)) {
    if (seen[r * src.gridW + c]) continue;
    order.push([r, c]);
  }

  // Le dessin fini, rendu UNE FOIS en grand, avant l'enregistrement : c'est lui que la loupe
  // parcourt. Agrandir les images de la vidéo aurait montré des pixels baveux, pas des touches ;
  // redessiner les cases visibles à chaque image aurait pu coûter, au plus large de la plongée,
  // tout le dessin trente fois par seconde. Le rendu prend quelques centaines de millisecondes sur
  // un téléphone, d'où la pause qui laisse d'abord la page afficher la progression.
  await new Promise((r) => setTimeout(r, 0));
  const zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, src.gridW / CLOSE_CELLS));
  const k = Math.min(zoom, DETAIL_MAX_PX / Math.max(sheetW, sheetH));
  const detail = document.createElement('canvas');
  detail.width = Math.round(sheetW * k);
  detail.height = Math.round(sheetH * k);
  const dg = detail.getContext('2d')!;
  dg.fillStyle = p.paper;
  dg.fillRect(0, 0, detail.width, detail.height);
  rule(dg, src, alpha.bareSheet && !alpha.ruledSheet, 0, 0, cw * k, cell * k, p.ruling);
  // Le rendu détaillé et le gros plan montrent la feuille FINIE : toutes ses cases posées, dit
  // explicitement — l'alphabet garde sinon l'état d'une partie du jeu sur la même grille.
  const all = src.target.map((row) => row.map(() => true));
  alpha.prepareGrid?.(src.target, 0, 0);
  alpha.prepareFilled?.(all);
  for (const [r, c] of order)
    alpha.draw(dg, c * cw * k, r * cell * k, cw * k, cell * k, src.target[r][c]);
  const [fx, fy] = focus(detail, src.gridW, src.gridH, zoom);

  // Le gros plan (cf. REGION_MARGIN) : la vue au grossissement `zIn`, centrée sur la cible et
  // gardée sur la feuille — la même règle que le cadre de la boucle, qui y atteint la cible au
  // même grossissement et n'en sort donc plus au-delà. Rendue au grossissement final.
  const zIn = zoom / REGION_MARGIN;
  const rw = sheetW / zIn;
  const rh = sheetH / zIn;
  const rx = Math.min(sheetW - rw, Math.max(0, fx * cw - rw / 2));
  const ry = Math.min(sheetH - rh, Math.max(0, fy * cell - rh / 2));
  const region = document.createElement('canvas');
  region.width = Math.round(rw * zoom);
  region.height = Math.round(rh * zoom);
  const rg = region.getContext('2d')!;
  rg.fillStyle = p.paper;
  rg.fillRect(0, 0, region.width, region.height);
  rule(
    rg,
    src,
    alpha.bareSheet && !alpha.ruledSheet,
    -rx * zoom,
    -ry * zoom,
    cw * zoom,
    cell * zoom,
    p.ruling,
  );
  // Les cases de la zone, et deux de plus tout autour : une touche de peinture déborde de sa case.
  const c0 = Math.floor(rx / cw) - 2;
  const c1 = Math.ceil((rx + rw) / cw) + 2;
  const r0 = Math.floor(ry / cell) - 2;
  const r1 = Math.ceil((ry + rh) / cell) + 2;
  alpha.prepareGrid?.(src.target, -rx * zoom, -ry * zoom);
  alpha.prepareFilled?.(all);
  for (const [r, c] of order)
    if (r >= r0 && r < r1 && c >= c0 && c < c1)
      alpha.draw(
        rg,
        (c * cw - rx) * zoom,
        (r * cell - ry) * zoom,
        cw * zoom,
        cell * zoom,
        src.target[r][c],
      );

  // L'ORIGINE de la feuille dans la vidéo, et pas (0, 0) : un alphabet qui calcule quelque chose
  // par case (l'angle des touches de peinture) retrouve la case d'après les coordonnées qu'il
  // reçoit. Sans elle, chaque touche prenait l'angle d'une case située ~ox/cw plus haut à gauche
  // (relevé à la revue du 2026-10-17).
  alpha.prepareGrid?.(src.target, ox, oy);

  const stream = cv.captureStream(FPS);
  const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 5_000_000 });
  const chunks: Blob[] = [];
  rec.ondataavailable = (e) => {
    if (e.data.size) chunks.push(e.data);
  };
  const done = new Promise<void>((resolve) => (rec.onstop = () => resolve()));
  rec.start(500);

  // Un minuteur et non requestAnimationFrame : l'enregistrement doit avancer même si l'écran
  // s'éteint ou que la page passe un instant au second plan — rAF s'y arrête net, et la vidéo
  // resterait figée à la case où il s'est arrêté.
  let drawn = 0;
  // Les cases déjà posées dans la vidéo (cf. stitchedSoFar.ts).
  const posed = noneStitched(src.target);
  const t0 = performance.now();
  await new Promise<void>((resolve) => {
    const tick = (): void => {
      const now = performance.now() - t0;
      const q = Math.min(1, now / FILL_MS);
      const z = now > FILL_MS ? loupeZoom(now - FILL_MS, zoom) : 1;
      // Départ franc, fin qui s'assied — la même courbe que la page de partage (src/share.ts).
      const due = Math.round((1 - Math.pow(1 - q, 2.1)) * order.length);
      // L'origine reposée à CHAQUE image, et pas une fois avant la boucle : l'alphabet ne garde
      // qu'une grille courante, et un rendu du jeu pendant les 11 s de l'enregistrement (rotation,
      // retour dans l'application) la remet à (0, 0) — la suite de la vidéo reprenait alors
      // l'angle d'une autre case (relevé à la revue du 2026-10-17). Sans coût : même grille, même
      // origine, l'appel rend la main tout de suite.
      // Les cases posées avec elle : un rendu du jeu a pu remplacer aussi l'état des voisines.
      if (drawn < due) {
        alpha.prepareGrid?.(src.target, ox, oy);
        alpha.prepareFilled?.(posed);
      }
      for (; drawn < due; drawn++) {
        const [r0, c0] = order[drawn];
        for (const [r, c] of placeCell(alpha, posed, r0, c0))
          alpha.draw(g, ox + c * cw, oy + r * cell, cw, cell, src.target[r][c]);
      }
      if (now > FILL_MS + LOUPE.rest) {
        // La loupe : le rendu détaillé, recadré. Le centre glisse du milieu de la feuille vers
        // l'endroit visé à mesure qu'on grossit — atteint dès `zIn`, où le gros plan prend le
        // relais —, et le cadre reste toujours sur la feuille.
        const t = Math.min(1, (z - 1) / (zIn - 1));
        const vw = sheetW / z;
        const vh = sheetH / z;
        const cx = Math.min(
          sheetW - vw / 2,
          Math.max(vw / 2, sheetW / 2 + (fx * cw - sheetW / 2) * t),
        );
        const cy = Math.min(
          sheetH - vh / 2,
          Math.max(vh / 2, sheetH / 2 + (fy * cell - sheetH / 2) * t),
        );
        const kx = detail.width / sheetW;
        const ky = detail.height / sheetH;
        g.imageSmoothingQuality = 'high';
        g.drawImage(
          detail,
          (cx - vw / 2) * kx,
          (cy - vh / 2) * ky,
          vw * kx,
          vh * ky,
          ox,
          oy,
          sheetW,
          sheetH,
        );
        // Le gros plan par-dessus, à sa vraie place dans la vue, fondu de 0 à 1 entre 80 % de
        // `zIn` et `zIn` : en dessous, la feuille entière suffit ; au-delà, elle serait floue.
        const a = Math.min(1, Math.max(0, (z - 0.8 * zIn) / (0.2 * zIn)));
        if (a > 0) {
          const s = sheetW / vw;
          g.save();
          g.beginPath();
          g.rect(ox, oy, sheetW, sheetH);
          g.clip();
          g.globalAlpha = a;
          g.drawImage(
            region,
            ox + (rx - (cx - vw / 2)) * s,
            oy + (ry - (cy - vh / 2)) * s,
            rw * s,
            rh * s,
          );
          g.restore();
        }
      } else if (q >= 1) {
        // Un trait invisible à chaque image : sans changement du canvas, captureStream n'émet plus
        // d'images, et le dessin fini ne tiendrait pas à l'écran.
        g.fillStyle = p.paper;
        g.fillRect(0, 0, 1, 1);
      }
      onProgress(Math.min(1, now / (FILL_MS + AFTER_MS)));
      if (now >= FILL_MS + AFTER_MS) resolve();
      else setTimeout(tick, 1000 / FPS);
    };
    tick();
  });
  rec.stop();
  await done;
  for (const t of stream.getTracks()) t.stop();
  const type = mime.split(';')[0];
  return { blob: new Blob(chunks, { type }), mime: type };
}

/** Les réglures de la feuille, sauf pour un alphabet qui joue sans (bareSheet, la peinture) :
 *  la vidéo doit montrer la feuille telle qu'on l'a jouée (relevé à la revue du 2026-10-17). */
function rule(
  g: CanvasRenderingContext2D,
  src: FillSource,
  bare: boolean | undefined,
  ox: number,
  oy: number,
  cw: number,
  cell: number,
  color: string,
): void {
  if (bare) return;
  g.strokeStyle = color;
  g.lineWidth = Math.max(0.5, Math.min(cw, cell) / 12);
  g.beginPath();
  for (let c = 0; c <= src.gridW; c++) {
    g.moveTo(ox + c * cw, oy);
    g.lineTo(ox + c * cw, oy + src.gridH * cell);
  }
  for (let r = 0; r <= src.gridH; r++) {
    g.moveTo(ox, oy + r * cell);
    g.lineTo(ox + src.gridW * cw, oy + r * cell);
  }
  g.stroke();
}

/** Le grossissement de la loupe `t` ms après la fin du remplissage : 1 au repos, `zoom` au plus
 *  près, et une courbe douce entre les deux — une caméra qui démarre et s'arrête sans à-coup. */
function loupeZoom(t: number, zoom: number): number {
  const ease = (x: number) => (x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2);
  // l'interpolation se fait sur le LOGARITHME du grossissement : en linéaire, la plongée filait
  // au début et traînait à la fin, chaque pas de ×0,1 comptant moins à mesure qu'on grossit
  const at = (x: number) => Math.exp(Math.log(zoom) * ease(Math.min(1, Math.max(0, x))));
  t -= LOUPE.rest;
  if (t < 0) return 1;
  if (t < LOUPE.dive) return at(t / LOUPE.dive);
  t -= LOUPE.dive;
  if (t < LOUPE.close) return zoom;
  t -= LOUPE.close;
  if (t < LOUPE.rise) return at(1 - t / LOUPE.rise);
  return 1;
}

/**
 * Où plonger : le centre, en cases, de la fenêtre de la loupe où le dessin fini CONTRASTE le plus
 * — un œil, une oreille sur un fond, un contour. Lu sur le rendu lui-même, ramené à un pixel par
 * case puis adouci d'une case : ce sont les clairs et les sombres qu'on voit.
 *
 * Pas sur les codes. La première version comptait les cases voisines de codes différents, et sur
 * le chaton noir du 2026-10-17 elle plongeait dans le canapé : le tramage y alterne trois gris à
 * chaque case, ce qui fait beaucoup de « bords » et rien à regarder. Le flou d'une case efface ce
 * grain et garde les vrais contours.
 */
function focus(detail: HTMLCanvasElement, gw: number, gh: number, zoom: number): [number, number] {
  const small = document.createElement('canvas');
  small.width = gw;
  small.height = gh;
  const sg = small.getContext('2d', { willReadFrequently: true })!;
  sg.imageSmoothingQuality = 'high';
  sg.drawImage(detail, 0, 0, gw, gh);
  const px = sg.getImageData(0, 0, gw, gh).data;
  const lum = new Float32Array(gw * gh);
  for (let i = 0; i < gw * gh; i++)
    lum[i] = 0.299 * px[i * 4] + 0.587 * px[i * 4 + 1] + 0.114 * px[i * 4 + 2];
  const at = (r: number, c: number) =>
    lum[Math.min(gh - 1, Math.max(0, r)) * gw + Math.min(gw - 1, Math.max(0, c))];
  const soft = new Float32Array(gw * gh);
  for (let r = 0; r < gh; r++)
    for (let c = 0; c < gw; c++) {
      let sum = 0;
      for (let dr = -1; dr <= 1; dr++) for (let dc = -1; dc <= 1; dc++) sum += at(r + dr, c + dc);
      soft[r * gw + c] = sum / 9;
    }
  const ww = Math.max(1, Math.round(gw / zoom));
  const wh = Math.max(1, Math.round(gh / zoom));
  // une table des sommes cumulées : la somme de chaque fenêtre en quatre lectures
  const sat = new Float64Array((gw + 1) * (gh + 1));
  for (let r = 0; r < gh; r++)
    for (let c = 0; c < gw; c++) {
      const v = soft[r * gw + c];
      const e =
        (c + 1 < gw ? Math.abs(soft[r * gw + c + 1] - v) : 0) +
        (r + 1 < gh ? Math.abs(soft[(r + 1) * gw + c] - v) : 0);
      sat[(r + 1) * (gw + 1) + c + 1] =
        e + sat[r * (gw + 1) + c + 1] + sat[(r + 1) * (gw + 1) + c] - sat[r * (gw + 1) + c];
    }
  // Une préférence pour le centre, un peu au-dessus du milieu : le sujet y est presque toujours,
  // et son visage plus haut que son corps. Sans elle, le contraste seul visait le bord le plus
  // franc de l'image — sur le Vermeer, le turban blanc sur le fond noir plutôt que le visage.
  const prior = (x: number, y: number) =>
    Math.exp(-(((x / gw - 0.5) / 0.28) ** 2 + ((y / gh - 0.42) / 0.28) ** 2) / 2);
  let best = -1;
  let found: [number, number] = [gw / 2, gh / 2];
  for (let r = 0; r + wh <= gh; r++)
    for (let c = 0; c + ww <= gw; c++) {
      const v =
        (sat[(r + wh) * (gw + 1) + c + ww] -
          sat[r * (gw + 1) + c + ww] -
          sat[(r + wh) * (gw + 1) + c] +
          sat[r * (gw + 1) + c]) *
        prior(c + ww / 2, r + wh / 2);
      if (v > best) {
        best = v;
        found = [c + ww / 2, r + wh / 2];
      }
    }
  return found;
}
